import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, Alert, Card, CircularProgress, Divider, Stack, Link as MuiLink } from '@mui/material';
import { keyframes } from '@emotion/react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

// A meghívó levél után látott bemutató: a platform előnyei (a /reszletek oldal stílusában)
const BENEFITS = [
  { icon: <StorefrontOutlinedIcon />, title: 'Új esküvői szolgáltatói piactér', text: 'Itt a párok aktívan szolgáltatókat keresnek, a profilodat is ők találják meg.' },
  { icon: <EventNoteOutlinedIcon />, title: 'Esküvő-tervező a párok oldalán', text: 'A párok a Wedigón tervezhetik és intézhetik az esküvőjüket egy helyen. Ha valami hiányzik a tervükből, könnyen találnak hozzájuk illő szolgáltatót. Lehet, pont te leszel az!' },
  { icon: <ChatBubbleOutlineIcon />, title: 'Teljes profil, közvetlen üzenetek', text: 'A szolgáltatói adataid láthatók a platformon, és a párok közvetlenül üzenhetnek neked.' },
  { icon: <FactCheckOutlinedIcon />, title: 'Saját ajánlatkérő űrlap', text: 'Összeállíthatod a kérdéseidet, és a pároktől minden fontos információt előre bekérhetsz az ajánlatadáshoz.' },
  { icon: <Inventory2OutlinedIcon />, title: 'Átlátható csomagok', text: 'A szolgáltatáscsomagjaidat áttekinthetően jelenítheted meg, így a párok könnyen választhatnak.' },
  { icon: <StarOutlineIcon />, title: 'Értékelések', text: 'A vendégek értékelései növelik a megbízhatóságodat a párok szemében.' },
  { icon: <PhotoCameraOutlinedIcon />, title: 'Fejezd be a profilod most', text: 'Tölts fel képeket, ellenőrizd az adataidat, és élesítsd a profilod.' },
];

const ProviderInviteWelcome = () => {
  // A tokenek az útvonalban vannak (/szolgaltatoi-udvozlo/:token/:decline), mert a levelező
  // kattintás-követése a query `=`/`&` jeleit elrontja (lásd providerInviteService.js)
  const { token, decline: declineToken } = useParams();

  const [loading, setLoading] = useState(!!token);
  const [invalid, setInvalid] = useState(!token);
  const [businessName, setBusinessName] = useState('');

  // A meghívó tokenhez tartozó előkészített profil neve — ugyanaz a végpont, mint a claim oldalé.
  // Token-váltáskor (SPA-navigáció) az előző állapot nem maradhat, ezért minden futáskor újraindul.
  useEffect(() => {
    setInvalid(false);
    setBusinessName('');
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    apiService.get(API_ENDPOINTS.AUTH.PROVIDER_INVITE_INFO(token))
      .then((response) => { if (active) setBusinessName(response?.data?.businessName || ''); })
      .catch(() => { if (active) setInvalid(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Lejárt / már beváltott / hibás link: a már beállított fiók a bejelentkezéshez irányítható
  if (invalid) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card variant="outlined" sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            Érvénytelen vagy lejárt meghívó link. Ha már beállította a jelszavát, jelentkezzen be. Egyéb esetben kérjük, keresse a kapcsolattartót.
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button component={Link} to="/login" variant="contained">Bejelentkezés</Button>
            <Button component={Link} to="/" variant="outlined">Főoldal</Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  const claimPath = `/szolgaltatoi-meghivo?token=${encodeURIComponent(token)}`;

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f7f0e0 0%, #f0ede6 45%, #eef1e9 100%)' }}>
      <Helmet>
        <title>Üdvözöljük a Wedigo-n | Wedigo</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Container maxWidth="md" sx={{ py: { xs: 6, sm: 8 }, px: { xs: 2.5, sm: 3 } }}>
        {/* ── Bemutatkozás ───────────────────────────────────── */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5 }, animation: `${fadeUp} 0.7s ease both` }}>
          <Typography variant="overline" sx={{ color: '#2a5d32', letterSpacing: '0.25em', fontWeight: 700 }}>
            Wedigo Partnerprogram
          </Typography>
          <Typography variant="h2" sx={{ mt: 1, fontSize: { xs: '2.1rem', sm: '2.7rem' }, color: '#1b331f', lineHeight: 1.15 }}>
            Üdvözöljük a Wedigo-n!
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, color: '#5b6e5d', maxWidth: 560, mx: 'auto', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
            {businessName ? <><strong>{businessName}</strong> profilja</> : 'A profilja'} már elkészült. Nézd át, mit kapsz a platformtól, és véglegesítsd szolgáltatásod profilját, hogy megjelenhess a párok előtt.
          </Typography>
        </Box>

        {/* ── Ingyenesség ─────────────────────────────────────── */}
        <Box sx={{ borderRadius: 3, p: { xs: 2.5, sm: 3 }, mb: { xs: 4, sm: 5 }, bgcolor: '#1b331f', color: '#fff', textAlign: 'center', animation: `${fadeUp} 0.7s ease both`, animationDelay: '0.1s' }}>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', sm: '1.2rem' }, color: '#edbe5f' }}>
            A regisztráció és a részvétel ingyenes
          </Typography>
          <Typography sx={{ mt: 1, fontSize: { xs: '0.95rem', sm: '1rem' }, opacity: 0.92 }}>
            Csak a platformon keresztül létrejött, sikeres foglalások után kell rendszerhasználati díjat fizetned. A díj pontos mértékéről a{' '}
            <MuiLink component={Link} to="/reszletek#arazas" sx={{ color: '#edbe5f', fontWeight: 600 }}>Részletek oldalon</MuiLink> tájékozódhatsz.
          </Typography>
        </Box>

        {/* ── Előnyök ─────────────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderColor: 'rgba(42,93,50,0.15)', bgcolor: 'rgba(255,255,255,0.6)', animation: `${fadeUp} 0.7s ease both`, animationDelay: '0.2s' }}>
          <Box sx={{ px: { xs: 2.5, sm: 3 }, py: { xs: 1, sm: 1.5 } }}>
            {BENEFITS.map((benefit, i) => (
              <React.Fragment key={benefit.title}>
                {i > 0 && <Divider sx={{ borderColor: 'rgba(42,93,50,0.1)' }} />}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5, py: { xs: 2, sm: 2.25 } }}>
                  <Box sx={{ flexShrink: 0, width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(42,93,50,0.08)', color: '#2a5d32' }}>
                    {benefit.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#1b331f', fontSize: { xs: '1rem', sm: '1.05rem' } }}>{benefit.title}</Typography>
                    <Typography sx={{ mt: 0.5, color: '#5b6e5d', fontSize: { xs: '0.9rem', sm: '0.95rem' } }}>{benefit.text}</Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Card>

        {/* ── CTA-k ───────────────────────────────────────────── */}
        <Stack spacing={2} alignItems="center" sx={{ mt: { xs: 5, sm: 6 }, animation: `${fadeUp} 0.7s ease both`, animationDelay: '0.3s' }}>
          <Button
            component={Link}
            to={claimPath}
            variant="contained"
            size="large"
            sx={{ py: 1.5, px: 4, width: { xs: '100%', sm: 'auto' }, bgcolor: '#edbe5f', color: '#1b331f', fontWeight: 700, '&:hover': { bgcolor: '#e2ac48' } }}
          >
            Profil véglegesítése
          </Button>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button component={Link} to="/szolgaltatok" variant="outlined" sx={{ color: '#2a5d32', borderColor: '#2a5d32' }}>
              Szétnézek a platformon
            </Button>
            <Button component={Link} to="/reszletek" variant="text" sx={{ color: '#2a5d32', fontWeight: 600 }}>
              Részletek a partnerprogramról
            </Button>
          </Stack>

          <Typography variant="body2" sx={{ color: '#5b6e5d', textAlign: 'center', maxWidth: 480 }}>
            A folytatásban megismerheted a{' '}
            <MuiLink component={Link} to="/partneri-aszf" target="_blank" rel="noopener noreferrer" sx={{ color: '#2a5d32', fontWeight: 600 }}>
              partneri ÁSZF-et
            </MuiLink>
            , az elfogadást a következő lépésben rögzítjük.
          </Typography>

          {declineToken && (
            <Button
              component={Link}
              to={`/meghivo-elutasitas/${encodeURIComponent(declineToken)}`}
              size="small"
              sx={{ color: '#8a958b', textTransform: 'none', fontWeight: 400 }}
            >
              Nem kérem a meghívást
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ProviderInviteWelcome;
